import { BuyerLegalType, ContractNature, FoaPart, Regulation } from "../../../../../autogen/bff-api";

export interface FoaPartBanner {
  messageKey: string;
  status: "info" | "warning";
  /** Interpolation values for the message — the legal basis the classification rests on. */
  values?: Record<string, string>;
}

// Mirrors the LOA floor in the BFF's ProcurementThresholds.kt — keep in sync.
export const OUTSIDE_FOA_THRESHOLD_NOK = 500_000;

export const isOutsideFoa = (estimatedValueNok: number | null | undefined): boolean =>
  estimatedValueNok != null && estimatedValueNok < OUTSIDE_FOA_THRESHOLD_NOK;

// Banner copy is consumed via a dynamic t(banner.messageKey) call, so langen can't see it.
// These magic comments register the keys statically — keep them verbatim with the switches below.
// t('This procurement is below the Procurement Act threshold — it should run in the simplified flow')
// t('This is a Del I procurement — it should run in the simplified flow')
// t('FOA Part II — national procurement, announced on Doffin')
// t('FOA Part III — over the EEA threshold, announced on Doffin and TED')
// t('FOA Part IV — special services')
// t('Choose the governing regulation to classify the procurement')
// t('Choose a contract type to classify the procurement')
// t('Set the buyer legal type below to classify the procurement')
// t('The procurement is classified when you continue')
// t('Forsyningsforskriften Part I — under the EEA threshold in {{provision}}. Only the basic principles apply, and there is no announcement duty')
// t('Forsyningsforskriften Part II — over the EEA threshold in {{provision}}. Announcement on Doffin and TED is required')
export interface ClassificationInputs {
  regulation: Regulation | null | undefined;
  foaPart: FoaPart | null | undefined;
  estimatedValueNok?: number | null;
  contractNature?: ContractNature | null;
  buyerLegalType?: BuyerLegalType | null;
}

export const foaPartBanner = ({
  regulation,
  foaPart,
  estimatedValueNok,
  contractNature,
  buyerLegalType,
}: ClassificationInputs): FoaPartBanner | null => {
  if (regulation != null && foaPart != null) {
    const provision = thresholdProvision(regulation, contractNature);
    return {
      messageKey: messageKeyFor(regulation, foaPart),
      status: "info",
      ...(provision ? { values: { provision } } : {}),
    };
  }
  // Unclassified: name the input that is still missing, but only once the buyer has given it
  // something to classify, and in the order the buyer meets them.
  if (estimatedValueNok == null) return null;
  if (regulation == null) {
    return { messageKey: "Choose the governing regulation to classify the procurement", status: "info" };
  }
  if (contractNature == null) {
    return { messageKey: "Choose a contract type to classify the procurement", status: "info" };
  }
  if (buyerLegalType == null && needsBuyerLegalType(regulation, contractNature)) {
    return { messageKey: "Set the buyer legal type below to classify the procurement", status: "warning" };
  }
  // Every input is in. The part itself is derived server-side, which for a draft that has not been
  // classified yet happens on continue — so promise that rather than naming an input as missing.
  return { messageKey: "The procurement is classified when you continue", status: "info" };
};

const messageKeyFor = (regulation: Regulation, foaPart: FoaPart): string => {
  switch (foaPart) {
    case FoaPart.OutsideFoa:
      return "This procurement is below the Procurement Act threshold — it should run in the simplified flow";
    case FoaPart.PartI:
      return regulation === Regulation.Foa
        ? "This is a Del I procurement — it should run in the simplified flow"
        : "Forsyningsforskriften Part I — under the EEA threshold in {{provision}}. Only the basic principles apply, and there is no announcement duty";
    case FoaPart.PartIi:
      return regulation === Regulation.Foa
        ? "FOA Part II — national procurement, announced on Doffin"
        : "Forsyningsforskriften Part II — over the EEA threshold in {{provision}}. Announcement on Doffin and TED is required";
    case FoaPart.PartIii:
      return "FOA Part III — over the EEA threshold, announced on Doffin and TED";
    case FoaPart.PartIv:
      return "FOA Part IV — special services";
    default: {
      const _exhaustive: never = foaPart;
      return _exhaustive;
    }
  }
};

// Mirrors ThresholdSet.eeaThresholdFor in the BFF's ProcurementThresholds.kt.
const thresholdProvision = (
  regulation: Regulation,
  contractNature: ContractNature | null | undefined,
): string | null => {
  if (regulation !== Regulation.Forsyningsforskriften) return null;
  return contractNature === ContractNature.Works ? "§ 5-2 (1) b" : "§ 5-2 (1) a";
};

// Mirrors ThresholdSet.eeaThresholdFor in the BFF's ProcurementThresholds.kt.
const needsBuyerLegalType = (
  regulation: Regulation,
  contractNature: ContractNature | null | undefined,
): boolean => regulation === Regulation.Foa && contractNature !== ContractNature.Works;

// Deliberately partial mirror of the BFF's RegulationSupportsPublishing gate (FieldKeys.kt): the BFF
// additionally blocks forsyning Del I, which reaches the UI through the readiness field instead.
export const regulationSupportsPublishing = (regulation: Regulation | null | undefined): boolean =>
  regulation !== Regulation.Konsesjonskontraktforskriften;
